import { useEffect, useState } from "react";

import formatCurrency from "@utils/formatCurrency";

import { InitialOrderDataPart, OrderFields } from "../constants";
import useOrderData from "../context/useOrderData";
import useOrderFieldsLoading from "../context/useOrderFieldsLoading";
import getIsOfferCurrent from "../utils/getIsOfferCurrent";
import useOrderFormik from "./useOrderFormik";

type TOrderPriceReturn = {
  price?: string;
  oldPrice?: string;
  rawPrice?: number;
};

const useOrderPrice = (): TOrderPriceReturn => {
  const [isInitialPriceLoaded, setIsInitialPriceLoaded] = useState(false);

  const {
    loadedInitialData,
    dynamicData: { offers },
  } = useOrderData();
  const { values } = useOrderFormik();

  const {
    [OrderFields.DELIVERY_METHOD]: deliveryMethod,
    [OrderFields.PROMO_CODE]: promoCode,
    [OrderFields.PRICE]: priceNumber,
    [OrderFields.ORIGINAL_PRICE]: originalPriceNumber,
  } = values;

  const { isFieldLoading } = useOrderFieldsLoading();

  const selectedOffer = offers.find(
    ({ deliveryMethod: offerDeliveryMethod }) => offerDeliveryMethod === deliveryMethod
  );

  const isPriceVisible =
    !!loadedInitialData[InitialOrderDataPart.PREFILL_VALUES] &&
    !isFieldLoading(OrderFields.OFFER_GROUP_ID) &&
    !!selectedOffer &&
    getIsOfferCurrent(selectedOffer, values);

  // Need to hide price initially, if we are loading a new price because there are some options selected
  // which have not default values
  // After initial load, when user changes options, old price can be displayed while new one is loading
  useEffect(() => {
    if (!isInitialPriceLoaded && isPriceVisible) {
      setIsInitialPriceLoaded(true);
    }
  }, [isInitialPriceLoaded, isPriceVisible]);

  const oldPriceNumber = promoCode ? originalPriceNumber : undefined;

  const price = isInitialPriceLoaded && typeof priceNumber !== "undefined" ? formatCurrency(priceNumber) : undefined;
  const oldPrice =
    isInitialPriceLoaded && typeof oldPriceNumber !== "undefined" ? formatCurrency(oldPriceNumber) : undefined;

  const rawPrice = isInitialPriceLoaded ? priceNumber : undefined;

  return { price, oldPrice, rawPrice };
};
export default useOrderPrice;
